import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'
import { useAuth } from '../../contexts/SupabaseAuthContext'
import { trackEvent } from '../../lib/meta-pixel'

interface LoginModalProps {
  isOpen: boolean
  onClose: () => void
  productName?: string
}

const LoginModal = ({ isOpen, onClose, productName = "Overpixel" }: LoginModalProps) => {
  const { signInWithGoogle, signInWithGooglePopup, loading, isAuthenticated } = useAuth()
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    return () => setMounted(false)
  }, [])

  // Fechar modal automaticamente quando autenticado
  useEffect(() => {
    if (isAuthenticated && isOpen) {
      onClose()
    }
  }, [isAuthenticated, isOpen, onClose])

  // Prevent scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  const handleGoogleLogin = async () => {
    // Track registration/login intent
    trackEvent('CompleteRegistration', {
      content_name: 'Google Login',
      status: 'initiated'
    })

    // Save current path to return after login
    if (typeof window !== 'undefined') {
      const currentPath = window.location.pathname + window.location.search
      window.localStorage.setItem('postLoginReturn', currentPath)
      console.log('LoginModal: Salvando retorno para:', currentPath)
    }
    
    // Tentar usar Popup para evitar sair da página (melhor UX)
    try {
      await signInWithGooglePopup()
    } catch (err) {
      console.warn('Popup falhou ou foi bloqueado, tentando redirect...', err)
      // Fallback para redirect se popup falhar
      await signInWithGoogle()
    }
  }

  if (!mounted || !isOpen) return null

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="relative w-full max-w-md bg-[#0A0A0A] border border-white/10 rounded-2xl overflow-hidden shadow-[0_0_50px_rgba(0,0,0,0.5)] animate-in zoom-in-95 duration-200">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors z-10 p-2 hover:bg-white/5 rounded-full"
        >
          <X size={20} />
        </button>

        {/* Content */}
        <div className="p-8 text-center">
          <div className="w-20 h-20 mx-auto bg-gradient-to-br from-cyber-blue/20 to-purple-500/20 rounded-full flex items-center justify-center mb-6 border border-white/5 shadow-inner">
            <span className="text-4xl">🔒</span>
          </div>

          <h3 className="text-2xl font-black text-white mb-2 tracking-tight">Login Necessário</h3>
          <p className="text-gray-400 mb-8 leading-relaxed">
            Para continuar com <span className="text-white font-medium">{productName}</span>, você precisa estar logado.
          </p>

          <button
            onClick={handleGoogleLogin}
            disabled={loading}
            className="w-full py-4 px-4 bg-white text-black font-bold rounded-xl hover:bg-gray-200 transition-all transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-3 shadow-lg shadow-white/10"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                Conectando...
              </span>
            ) : (
              <>
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.84z" />
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                <span>Entrar com Google</span>
              </>
            )}
          </button>

          <p className="mt-6 text-xs text-gray-600">
            Ambiente seguro. Seus dados estão protegidos.
          </p>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default LoginModal
